import { useState, useEffect } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import type { AuthUser } from 'aws-amplify/auth';
import ClientManager   from './ClientManager';
import AudienceBuilder from './AudienceBuilder';
import PixelInstaller  from './PixelInstaller';
import ContactList     from './ContactList';
import Overview        from './Overview';
import Help            from './Help';
import Leads           from './Leads';
import Credits         from './Credits';
import Billing         from './Billing';

type Tab = 'overview' | 'clients' | 'credits' | 'bill' | 'contacts' | 'audiences' | 'leads' | 'pixel' | 'help';

const NAV: { id: Tab; icon: string; label: string }[] = [
  { id: 'overview',   icon: '◈', label: 'Overview'   },
  { id: 'clients',    icon: '⬡', label: 'Clients'    },
  { id: 'credits',    icon: '◈', label: 'Credits'    },
  { id: 'bill',       icon: '🧾', label: 'Bill'       },
  { id: 'contacts',   icon: '⊞', label: 'Contacts'   },
  { id: 'audiences',  icon: '◎', label: 'Audiences'  },
  { id: 'leads',      icon: '📨', label: 'Leads'      },
  { id: 'pixel',      icon: '◉', label: 'Pixel'      },
  { id: 'help',       icon: '?', label: 'Help'       },
];

export default function Dashboard({
  user, onSignOut,
}: { user: AuthUser; onSignOut: () => void }) {
  const [tab, setTab] = useState<Tab>('overview');
  const [leadsInitSource, setLeadsInitSource] = useState<string | undefined>(undefined);
  const [displayName, setDisplayName] = useState('');
  const [initials,    setInitials]    = useState('A');

  function handleNavigate(t: Tab, params?: { source?: string }) {
    setLeadsInitSource(t === 'leads' ? params?.source : undefined);
    setTab(t);
  }

  useEffect(() => {
    fetchUserAttributes().then(attrs => {
      const first = attrs.given_name  ?? '';
      const last  = attrs.family_name ?? '';
      const full  = [first, last].filter(Boolean).join(' ');
      if (full) {
        setDisplayName(full);
        setInitials((first[0] ?? last[0] ?? 'A').toUpperCase());
      } else {
        // fallback to email prefix
        const email = (user as any).signInDetails?.loginId ?? user.username ?? '';
        setDisplayName(email.split('@')[0]);
        setInitials((email[0] ?? 'A').toUpperCase());
      }
    }).catch(() => {
      const email = (user as any).signInDetails?.loginId ?? user.username ?? '';
      setDisplayName(email.split('@')[0]);
      setInitials((email[0] ?? 'A').toUpperCase());
    });
  }, [user]);

  return (
    <div style={{
      display: 'flex', height: '100vh', overflow: 'hidden',
      background: '#080c14',
    }}>
      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside style={{
        width: 220, flexShrink: 0,
        background: '#080c14',
        borderRight: '1px solid #111927',
        display: 'flex', flexDirection: 'column',
        padding: '0',
      }}>
        {/* Logo */}
        <div style={{
          padding: '22px 20px 18px',
          borderBottom: '1px solid #111927',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src="https://realtimerx-static-assets.s3.us-east-1.amazonaws.com/icons/intentflo_facebook_profile_1024x1024.jpg"
              alt="intentFlo"
              style={{
                width: 34, height: 34, borderRadius: 9,
                flexShrink: 0, objectFit: 'cover',
                boxShadow: '0 0 16px rgba(59,130,246,0.22)',
              }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.02em', color: '#e8edf5', lineHeight: 1.2 }}>
                intent<span style={{ color: '#3b82f6' }}>flo</span>
              </div>
              <div style={{ fontSize: 10, color: '#3b4f6a', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Admin Console
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map(n => {
            const active = tab === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setTab(n.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 8,
                  background: active ? 'rgba(59,130,246,0.12)' : 'transparent',
                  border:     active ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
                  color:      active ? '#60a5fa' : '#4a5d7a',
                  cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                  fontFamily: 'Sora, sans-serif',
                  transition: 'all 0.15s', textAlign: 'left', width: '100%',
                }}
                onMouseEnter={e => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.background = '#0d1420';
                    (e.currentTarget as HTMLElement).style.color = '#8a9bb5';
                  }
                }}
                onMouseLeave={e => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = '#4a5d7a';
                  }
                }}
              >
                <span style={{ fontSize: 16, width: 18, textAlign: 'center', flexShrink: 0 }}>
                  {n.icon}
                </span>
                {n.label}
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div style={{
          padding: '14px 16px',
          borderTop: '1px solid #111927',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1e3a5f, #0f2440)',
            border: '1px solid #1e2d42',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: '#60a5fa', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e8edf5',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </div>
            <div style={{ fontSize: 10, color: '#3b82f6', letterSpacing: '0.06em' }}>ADMIN</div>
          </div>
          <button
            onClick={onSignOut}
            title="Sign out"
            style={{
              background: 'none', border: 'none', color: '#4a5d7a',
              cursor: 'pointer', fontSize: 14, padding: 4, flexShrink: 0,
            }}
          >⎋</button>
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────── */}
      <main style={{
        flex: 1, overflow: 'auto',
        background: '#080c14',
      }}>
        {/* Top bar */}
        <div style={{
          height: 52, padding: '0 28px',
          borderBottom: '1px solid #111927',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#080c14', position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ fontSize: 13, color: '#4a5d7a' }}>
            {NAV.find(n => n.id === tab)?.icon}{' '}
            <span style={{ color: '#8a9bb5' }}>{NAV.find(n => n.id === tab)?.label}</span>
          </div>
          <div style={{
            fontSize: 11, color: '#1e2d42', fontFamily: 'IBM Plex Mono, monospace',
          }}>
            {new Date().toISOString().slice(0, 10)}
          </div>
        </div>

        {/* Page content */}
        <div style={{ padding: '28px' }} className="fade-in" key={tab}>
          {tab === 'overview'   && <Overview   onNavigate={handleNavigate} />}
          {tab === 'clients'    && <ClientManager onNavigate={setTab} />}
          {tab === 'audiences'  && <AudienceBuilder />}
          {tab === 'pixel'      && <PixelInstaller />}
          {tab === 'contacts'   && <ContactList />}
          {tab === 'leads'      && <Leads initialSource={leadsInitSource} />}
          {tab === 'credits'    && <Credits />}
          {tab === 'bill'       && <Billing />}
          {tab === 'help'       && <Help />}
        </div>
      </main>
    </div>
  );
}
